/**
 * 營運中心清單頁
 * func_code: ee_ops_centers（路由 /ee_ops_centers）
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Button, TextField, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  InputAdornment, Tooltip, FormControlLabel, Switch, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Slideshow as SlideshowIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../components/FunctionPageHeader';
import { useMessage } from '../contexts/MessageContext';
import { logView, logCreate, logUpdate, logDelete } from '../utils/userLogHelper';
import { usePermission } from '../hooks/usePermission';
import {
  listCenters, createCenter, updateCenter, deleteCenter,
} from '../services/opsCenterService';
import { listTopics } from '../services/researchService';
import { OpsCenter, OpsCenterCreate, OpsCenterUpdate } from '../types/opsCenter';
import { Topic } from '../types/research';

const FUNC_CODE = 'ee_ops_centers';

const OpsCentersPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canUpdate = hasPermission(FUNC_CODE, 'update');
  const canDelete = hasPermission(FUNC_CODE, 'delete');

  const [centers, setCenters] = useState<OpsCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [topics, setTopics] = useState<Topic[]>([]);

  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState<OpsCenterCreate>({
    code: '', name: '', description: '', research_topic_code: '',
  });

  // 編輯對話框
  const [editTarget, setEditTarget] = useState<OpsCenter | null>(null);
  const [editForm, setEditForm] = useState<OpsCenterUpdate>({});

  const openEdit = (c: OpsCenter) => {
    setEditTarget(c);
    setEditForm({
      name: c.name,
      description: c.description ?? '',
      research_topic_code: c.research_topic_code ?? '',
      is_active: c.is_active,
    });
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    try {
      const updated = await updateCenter(editTarget.id, editForm);
      logUpdate(FUNC_CODE, { ...editTarget }, { ...updated });
      showSuccess('SYS020002', { name: editForm.name ?? editTarget.name });
      setEditTarget(null);
      await load();
    } catch (e: any) {
      logUpdate(FUNC_CODE, { ...editTarget }, editForm, String(e?.message || e));
      showApiError(e);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await listCenters({ search: search || undefined });
      setCenters(data);
      logView(FUNC_CODE, { search: search || undefined, count: data.length });
    } catch (e: any) {
      logView(FUNC_CODE, { search: search || undefined }, String(e?.message || e));
      showApiError(e);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // 載入啟用中的議題清單供 dropdown 選用
  useEffect(() => {
    (async () => {
      try {
        const ts = await listTopics({ is_active: true });
        setTopics(ts);
      } catch { /* 議題模組沒啟用時容錯，下拉就空 */ } // silent ok: optional module not enabled, non-blocking
    })();
  }, []);

  const handleCreate = async () => {
    try {
      const created = await createCenter(form);
      logCreate(FUNC_CODE, { ...created });
      showSuccess('SYS020001', { name: created.name });
      setOpenCreate(false);
      setForm({ code: '', name: '', description: '', research_topic_code: '' });
      await load();
    } catch (e: any) {
      logCreate(FUNC_CODE, form, String(e?.message || e));
      showApiError(e);
    }
  };

  const handleDelete = async (c: OpsCenter) => {
    if (!window.confirm(t('opsCenter.list.deleteConfirm', { name: c.name }))) return;
    try {
      await deleteCenter(c.id);
      logDelete(FUNC_CODE, { ...c });
      showSuccess('SYS020003', { name: c.name });
      await load();
    } catch (e: any) {
      logDelete(FUNC_CODE, { ...c }, String(e?.message || e));
      showApiError(e);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <FunctionPageHeader funcCode="ee_ops_centers" fallback={t('opsCenter.list.header')} />

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t('opsCenter.list.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 280 }}
        />
        <Button variant="outlined" onClick={load}>{t('opsCenter.list.refresh')}</Button>
        <Box sx={{ flexGrow: 1 }} />
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setOpenCreate(true)}>
            {t('opsCenter.list.addNew')}
          </Button>
        )}
      </Paper>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('opsCenter.list.headers.id')}</TableCell>
              <TableCell>{t('opsCenter.list.headers.code')}</TableCell>
              <TableCell>{t('opsCenter.list.headers.name')}</TableCell>
              <TableCell>{t('opsCenter.list.headers.sourceTopic')}</TableCell>
              <TableCell align="center">{t('opsCenter.list.headers.materialCount')}</TableCell>
              <TableCell align="center">{t('opsCenter.list.headers.status')}</TableCell>
              <TableCell align="center" width={220}>{t('opsCenter.list.headers.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} align="center">{t('opsCenter.list.loading')}</TableCell></TableRow>}
            {!loading && centers.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">{t('opsCenter.list.empty')}</TableCell></TableRow>
            )}
            {centers.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>#{c.id}</TableCell>
                <TableCell><code>{c.code}</code></TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.name}</Typography>
                  {c.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 500 }}>
                      {c.description.slice(0, 120)}{c.description.length > 120 ? '…' : ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {c.research_topic_code ? <Chip size="small" label={c.research_topic_code} /> : '-'}
                </TableCell>
                <TableCell align="center">{c.material_count ?? 0}</TableCell>
                <TableCell align="center">
                  <Chip size="small"
                        label={c.is_active ? t('opsCenter.list.active') : t('opsCenter.list.inactive')}
                        color={c.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={t('opsCenter.list.tooltips.open')}>
                    <IconButton size="small"
                                onClick={() => navigate(`/ee_ops_centers/${c.code}`)}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('opsCenter.list.tooltips.present')}>
                    <IconButton size="small" color="primary"
                                onClick={() => navigate(`/ee_ops_centers/${c.code}/present`)}>
                      <SlideshowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canUpdate && (
                    <Tooltip title={t('opsCenter.list.tooltips.edit')}>
                      <IconButton size="small" onClick={() => openEdit(c)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title={t('opsCenter.list.tooltips.delete')}>
                      <IconButton size="small" color="error" onClick={() => handleDelete(c)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* 編輯對話框 */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editTarget && t('opsCenter.list.editDialog.title', { name: editTarget.name })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label={t('opsCenter.list.editDialog.name')}
                       value={editForm.name ?? ''}
                       onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                       required fullWidth />
            <TextField label={t('opsCenter.list.editDialog.description')}
                       value={editForm.description ?? ''}
                       onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                       multiline rows={3} fullWidth />
            <Autocomplete
              options={topics}
              getOptionLabel={(opt) => `${opt.topic_code} · ${opt.topic_name}`}
              isOptionEqualToValue={(opt, val) => opt.topic_code === val.topic_code}
              value={topics.find((t) => t.topic_code === editForm.research_topic_code) ?? null}
              onChange={(_, val) => setEditForm({ ...editForm, research_topic_code: val?.topic_code ?? '' })}
              renderInput={(params) => (
                <TextField {...params}
                  label={t('opsCenter.list.editDialog.researchTopic')}
                  helperText={t('opsCenter.list.createDialog.researchTopicHelper')}
                />
              )}
            />
            <FormControlLabel
              control={<Switch checked={editForm.is_active ?? true}
                               onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />}
              label={t('opsCenter.list.editDialog.active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>{t('opsCenter.list.editDialog.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveEdit}
                  disabled={!editForm.name || !editForm.name.trim()}>
            {t('opsCenter.list.editDialog.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('opsCenter.list.createDialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label={t('opsCenter.list.createDialog.code')} value={form.code}
                       onChange={(e) => setForm({ ...form, code: e.target.value })}
                       required fullWidth />
            <TextField label={t('opsCenter.list.createDialog.name')} value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       required fullWidth />
            <TextField label={t('opsCenter.list.createDialog.description')} value={form.description}
                       onChange={(e) => setForm({ ...form, description: e.target.value })}
                       multiline rows={3} fullWidth />
            <Autocomplete
              options={topics}
              getOptionLabel={(opt) => `${opt.topic_code} · ${opt.topic_name}`}
              isOptionEqualToValue={(opt, val) => opt.topic_code === val.topic_code}
              value={topics.find((t) => t.topic_code === form.research_topic_code) ?? null}
              onChange={(_, val) => setForm({ ...form, research_topic_code: val?.topic_code ?? '' })}
              renderInput={(params) => (
                <TextField {...params}
                  label={t('opsCenter.list.createDialog.researchTopic')}
                  helperText={t('opsCenter.list.createDialog.researchTopicHelper')}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>{t('opsCenter.list.createDialog.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate}
                  disabled={!form.code || !form.name}>
            {t('opsCenter.list.createDialog.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpsCentersPage;
